import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { flushSync } from "react-dom"

type Theme = "light" | "dark" | "system"

interface ThemeContextValue {
  theme: Theme
  resolvedTheme: "light" | "dark"
  setTheme: (theme: Theme, origin?: { x: number; y: number }) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const THEME_COOKIE = "hivehook_theme"

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light"
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light"
}

// Theme lives in a cookie scoped to .hivehook.com so the choice carries
// between the marketing site and the app.
function readThemeCookie(): Theme | null {
  const m = document.cookie.match(/(?:^|; )hivehook_theme=(light|dark|system)/)
  return m ? (m[1] as Theme) : null
}

function writeThemeCookie(theme: Theme) {
  const h = location.hostname
  const scope =
    h === "hivehook.com" || h.endsWith(".hivehook.com") ? "; domain=.hivehook.com" : ""
  document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000; samesite=lax${scope}`
}

type ViewTransitionDoc = Document & {
  startViewTransition?: (cb: () => void) => unknown
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    return readThemeCookie() ?? "system"
  })

  const [systemTheme, setSystemTheme] = useState<"light" | "dark">(
    getSystemTheme,
  )

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    const handler = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? "dark" : "light")
    }
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const resolvedTheme = theme === "system" ? systemTheme : theme

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove("light", "dark")
    root.classList.add(resolvedTheme)
    root.style.colorScheme = resolvedTheme
  }, [resolvedTheme])

  const setTheme = useCallback(
    (t: Theme, origin?: { x: number; y: number }) => {
      const apply = () => {
        flushSync(() => {
          setThemeState(t)
        })
        writeThemeCookie(t)
      }

      const doc = document as ViewTransitionDoc
      const prefersReduced =
        window.matchMedia("(prefers-reduced-motion: reduce)").matches

      if (!doc.startViewTransition || !origin || prefersReduced) {
        apply()
        return
      }

      const root = document.documentElement
      root.style.setProperty("--theme-toggle-x", origin.x + "px")
      root.style.setProperty("--theme-toggle-y", origin.y + "px")
      doc.startViewTransition(apply)
    },
    [],
  )

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  )

  return <ThemeContext value={value}>{children}</ThemeContext>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error("useTheme must be used within ThemeProvider")
  }
  return ctx
}
